import { useState, useMemo } from 'react';
import Tooltip from '../Tooltip';
import GirderConnection from './GirderConnection';
import SyncHistoryModal from './sync/SyncHistoryModal';
import styles from './ConnectionsList.module.css';

/**
 * ConnectionsList component - displays connections with status filter buttons
 */
export default function ConnectionsList({
    connections,
    mode,
    role,
    connectedList,
    mutations,
    onRefresh
}) {
    const [activeFilters, setActiveFilters] = useState(new Set());
    const [allHistoryOpen, setAllHistoryOpen] = useState(false);
    
    // Available status types
    const statusTypes = [
        { key: 'initiated', label: 'Initiated' },
        { key: 'unverified', label: 'Unverified' },
        { key: 'awaiting-approval', label: 'Awaiting Approval' },
        { key: 'connecting', label: 'Connecting' },
        { key: 'connected', label: 'Connected' },
        { key: 'canceled', label: 'Canceled' },
        { key: 'rejected', label: 'Rejected' },
        { key: 'terminated', label: 'Terminated' },
        { key: 'unexpected', label: 'Needs attention (unexpected)' },
        { key: 'failed', label: 'Needs attention (failed)' },
    ];
    
    // Toggle filter for a status
    const toggleFilter = (statusKey) => {
        setActiveFilters(prev => {
            const newFilters = new Set(prev);
            if (newFilters.has(statusKey)) {
                newFilters.delete(statusKey);
            } else {
                newFilters.add(statusKey);
            }
            return newFilters;
        });
    };
    
    // Group connections by parent and build nested structure
    // Backend returns unified structure already grouped by girderNetworkUuid
    // Each item has: girderNetworkUuid, outboundConnection, inboundConnection, networkNode, proxiedNode
    const groupedConnections = useMemo(() => {
        // Work directly with unified groups instead of extracting connections
        // This avoids duplication issues with bidirectional connections
        const parents = [];
        const childrenByParent = new Map();
        const parentConnectionsByGroup = new Map(); // Map unified group to parent connection
        
        connections.forEach(group => {
            // Check if this unified group is a parent or child
            // A group is a parent if neither outbound nor inbound connection has a parentConnectionId
            const outboundParentId = group.outboundConnection?.parentConnectionId;
            const inboundParentId = group.inboundConnection?.parentConnectionId;
            const parentId = outboundParentId || inboundParentId;
            
            if (parentId) {
                // This is a child - add to childrenByParent
                if (!childrenByParent.has(parentId)) {
                    childrenByParent.set(parentId, []);
                }
                childrenByParent.get(parentId).push(group);
            } else {
                // This is a parent - we need a connection object for the parent
                // Use outboundConnection if available, otherwise inboundConnection
                const parentConnection = group.outboundConnection || group.inboundConnection;
                if (parentConnection) {
                    parents.push(parentConnection);
                    // Store mapping from parent connection to unified group for reference
                    parentConnectionsByGroup.set(parentConnection._id, group);
                }
            }
        });
        
        // Build result: parents with their children
        const result = [];
        parents.forEach(parent => {
            const children = childrenByParent.get(parent._id) || [];
            
            // Also include proxied-only nodes for this parent (no outbound/inbound after switch)
            const parentGroup = parentConnectionsByGroup.get(parent._id);
            const parentNodeId = parent.parentNodeId || parentGroup?.networkNode?._id;
            if (parentNodeId) {
                const parentNodeKey = String(parentNodeId);
                connections.forEach(group => {
                    if (group.proxiedNode && !group.outboundConnection && !group.inboundConnection) {
                        const proxyKey = group.proxiedNode.proxyNodeId != null
                            ? String(group.proxiedNode.proxyNodeId)
                            : null;
                        if (proxyKey === parentNodeKey) {
                            const alreadyAdded = children.some(child =>
                                child.girderNetworkUuid === group.girderNetworkUuid
                            );
                            if (!alreadyAdded) {
                                children.push(group);
                            }
                        }
                    }
                });
            }
            
            result.push({ 
                connection: parent, 
                children: children,
                networkNode: parentGroup?.networkNode || null,
            });
        });
        
        return result;
    }, [connections]);
    
    // Filter connections based on active filters
    // If a parent matches, include it and all its children
    // If only a child matches, include it with its parent
    const filteredGroupedConnections = useMemo(() => {
        if (activeFilters.size === 0) {
            return groupedConnections;
        }
        
        return groupedConnections
            .map(({ connection, children, networkNode }) => {
                const parentMatches = activeFilters.has(connection.status);
                const matchingChildren = children.filter(child => {
                    const outboundStatus = child.outboundConnection?.status;
                    const inboundStatus = child.inboundConnection?.status;
                    return activeFilters.has(outboundStatus) || activeFilters.has(inboundStatus);
                });
                
                // Include if parent matches or any child matches
                if (parentMatches || matchingChildren.length > 0) {
                    return {
                        connection,
                        children: parentMatches ? children : matchingChildren,
                        networkNode,
                    };
                }
                return null;
            })
            .filter(item => item !== null);
    }, [groupedConnections, activeFilters]);
    
    // Extract parent connections for status counts
    // Use same logic as useNetworkData for consistency
    const parentConnections = useMemo(() => {
        const parentConns = [];
        const seenConnectionIds = new Set();
        
        connections.forEach(group => {
            const outboundConn = group.outboundConnection;
            if (outboundConn && !outboundConn.parentConnectionId) {
                const connId = outboundConn._id;
                if (!seenConnectionIds.has(connId)) {
                    parentConns.push(outboundConn);
                    seenConnectionIds.add(connId);
                }
            }
            
            const inboundConn = group.inboundConnection;
            if (inboundConn && !inboundConn.parentConnectionId) {
                const connId = inboundConn._id;
                if (connId !== outboundConn?._id && !seenConnectionIds.has(connId)) {
                    parentConns.push(inboundConn);
                    seenConnectionIds.add(connId);
                }
            }
        });
        
        return parentConns;
    }, [connections]);
    
    // Count connections by status (only parent connections)
    const statusCounts = useMemo(() => {
        const counts = {};
        parentConnections.forEach(conn => {
            const status = conn.status;
            counts[status] = (counts[status] || 0) + 1;
        });
        return counts;
    }, [parentConnections]);
    
    return (
        <div className={styles.connectionsListContainer}>
            <div className={styles.listHeader}>
                <h3>Network Connections</h3>
                <div className={styles.listHeaderActions}>
                    <button
                        type="button"
                        className={styles.historyButton}
                        onClick={() => setAllHistoryOpen(true)}
                    >
                        Sync history
                    </button>
                    <Tooltip text="Refresh list" position="top">
                        <button
                            onClick={onRefresh}
                            className={styles.refreshButton}
                        >
                            ↻
                        </button>
                    </Tooltip>
                </div>
            </div>
            <SyncHistoryModal
                open={allHistoryOpen}
                onClose={() => setAllHistoryOpen(false)}
            />
            
            <div className={styles.filterButtons}>
                {statusTypes.map(status => {
                    const count = statusCounts[status.key] || 0;
                    const isActive = activeFilters.has(status.key);
                    
                    if (count === 0 && !isActive) {
                        return null; // Don't show buttons for statuses with no connections
                    }
                    
                    return (
                        <button
                            key={status.key}
                            className={`${styles.filterButton} ${isActive ? styles.filterButtonActive : ''}`}
                            onClick={() => toggleFilter(status.key)}
                            title={`${isActive ? 'Hide' : 'Show'} ${status.label} connections`}
                        >
                            {status.label}
                            {count > 0 && (
                                <span className={styles.filterCount}>({count})</span>
                            )}
                        </button>
                    );
                })}
                {activeFilters.size > 0 && (
                    <button
                        className={styles.clearFiltersButton}
                        onClick={() => setActiveFilters(new Set())}
                        title="Clear all filters"
                    >
                        Clear Filters
                    </button>
                )}
            </div>
            
            <div className={styles.connectionsList}>
                {filteredGroupedConnections.length === 0 ? (
                    <div className={styles.noConnections}>
                        {activeFilters.size > 0 
                            ? 'No connections match the selected filters.'
                            : 'No connections found.'}
                    </div>
                ) : (
                    filteredGroupedConnections.map(({ connection, children, networkNode }) => (
                        <GirderConnection
                            key={connection._id}
                            connection={connection}
                            networkNode={networkNode}
                            mode={mode}
                            role={role}
                            connectedList={connectedList}
                            mutations={mutations}
                            childConnections={children}
                        />
                    ))
                )}
            </div>
        </div>
    );
}

